<template>
  <!--
    Throwaway admin harness for proving the WhatsApp Cloud API send path works.
    Labels are hardcoded English on purpose: this page is meant to be deleted
    once the real reminder feature ships, so it stays out of translationMixin.
  -->
  <div class="whatsapp-test-page" dir="ltr">
    <h1 class="page-title">WhatsApp send test</h1>
    <p class="page-subtitle">
      Admin only. Sends a real message and shows exactly what Meta answered.
    </p>

    <div v-if="loadingConfig" class="panel">Loading configuration&hellip;</div>

    <div v-else-if="configError" class="panel panel-error">
      <strong>{{ configError }}</strong>
      <p v-if="configErrorHint" class="hint">{{ configErrorHint }}</p>
    </div>

    <template v-else>
      <div class="panel" :class="serverConfig.configured ? 'panel-ok' : 'panel-warn'">
        <div class="config-row">
          <span>Credentials</span>
          <strong>{{ serverConfig.configured ? 'configured' : 'MISSING' }}</strong>
        </div>
        <div class="config-row">
          <span>Phone number ID</span>
          <code>{{ serverConfig.phone_number_id || '—' }}</code>
        </div>
        <div class="config-row">
          <span>WABA ID</span>
          <code>{{ serverConfig.waba_id || '—' }}</code>
        </div>
        <div class="config-row">
          <span>Graph API version</span>
          <code>{{ serverConfig.api_version }}</code>
        </div>
        <div class="config-row">
          <span>Environment</span>
          <strong>{{ serverConfig.is_production ? 'production' : 'NOT production' }}</strong>
        </div>
        <div v-if="!serverConfig.is_production" class="config-row">
          <span>WHATSAPP_TEST_RECIPIENT</span>
          <strong>{{ serverConfig.test_recipient_configured ? 'configured' : 'not set' }}</strong>
        </div>
        <p v-if="!serverConfig.configured" class="hint">
          WHATSAPP_TOKEN and WHATSAPP_PHONE_NUMBER_ID must both be set in the
          environment the backend is running in.
        </p>
        <!-- Said before Send is pressed, because the destination shown in the
             form below is not where a non-production send actually goes. -->
        <p v-if="willSuppress" class="hint hint-warn">
          This backend is not production (DATABASE_URL is unset) and no
          WHATSAPP_TEST_RECIPIENT is configured, so sends are SUPPRESSED: nothing
          will reach any handset. Set WHATSAPP_TEST_RECIPIENT to your own number.
        </p>
        <p v-else-if="willRedirect" class="hint hint-warn">
          This backend is not production, so every send is redirected to
          WHATSAPP_TEST_RECIPIENT regardless of the number typed below.
        </p>
      </div>

      <div class="panel">
        <div class="templates-head">
          <span class="field-label">Templates in this account</span>
          <button class="link-button" :disabled="loadingTemplates" @click="loadTemplates">
            {{ loadingTemplates ? 'checking…' : 'refresh' }}
          </button>
        </div>
        <p v-if="templatesError" class="hint hint-warn">{{ templatesError }}</p>
        <p v-else-if="!templates.length && !loadingTemplates" class="hint hint-warn">
          No templates exist yet. A cold send needs an approved template, so any
          send will fail with 132001 until one is created and approved.
        </p>
        <table v-else-if="templates.length" class="templates">
          <tr v-for="t in templates" :key="`${t.name}-${t.language}`">
            <td><code>{{ t.name }}</code></td>
            <td><code>{{ t.language }}</code></td>
            <td>
              <span :class="['badge', statusClass( t.status )]">{{ t.status }}</span>
            </td>
            <td>
              <button class="link-button" @click="useTemplate( t )">use</button>
            </td>
          </tr>
        </table>
        <p v-if="hasPendingTemplate" class="hint">
          PENDING means Meta is still reviewing it. Sending will fail until it
          flips to APPROVED, usually a few minutes for a utility template.
        </p>
      </div>

      <div class="panel">
        <label class="field">
          <span class="field-label">Send to</span>
          <input
            v-model="phone"
            type="tel"
            class="field-input"
            placeholder="0526867915 or +972526867915"
            :disabled="sending"
          />
          <span v-if="normalizedPhone" class="hint">
            Will send to <code>+{{ normalizedPhone }}</code>
          </span>
          <span v-else-if="phone" class="hint hint-warn">
            Not enough digits yet.
          </span>
        </label>

        <div class="field">
          <span class="field-label">What to send</span>
          <label class="radio">
            <input type="radio" value="template" v-model="mode" :disabled="sending" />
            Approved template
            <span class="radio-note">— the only thing that works for a cold send</span>
          </label>
          <label class="radio">
            <input type="radio" value="text" v-model="mode" :disabled="sending" />
            Free text
            <span class="radio-note">— only if they messaged you in the last 24h</span>
          </label>
        </div>

        <template v-if="mode === 'template'">
          <div class="field-pair">
            <label class="field">
              <span class="field-label">Template name</span>
              <input v-model="templateName" type="text" class="field-input" :disabled="sending" />
            </label>
            <label class="field">
              <span class="field-label">Language code</span>
              <input v-model="languageCode" type="text" class="field-input" :disabled="sending" />
            </label>
          </div>
          <label class="field">
            <span class="field-label">Body parameters</span>
            <input
              v-for="(param, index) in params"
              :key="index"
              v-model="params[index]"
              type="text"
              class="field-input param-input"
              :placeholder="paramPlaceholder( index )"
              :disabled="sending"
            />
            <span class="hint">
              Leave empty for <code>hello_world</code>, which takes no parameters.
            </span>
          </label>
        </template>

        <label v-else class="field">
          <span class="field-label">Message text</span>
          <textarea
            v-model="textBody"
            rows="3"
            class="field-input"
            placeholder="Hello from the trip planner"
            :disabled="sending"
          ></textarea>
        </label>

        <button
          class="send-button"
          :disabled="sending || !canSend"
          @click="send"
        >
          {{ sending ? 'Sending…' : 'Send message' }}
        </button>
      </div>

      <!-- A suppressed send gets its own non-green panel: it is neither a
           success nor a failure, and calling it either would make this page lie
           about whether anything was sent. -->
      <div v-if="result" class="panel" :class="resultPanelClass">
        <h2 class="result-title">{{ resultTitle }}</h2>
        <div v-if="result.sent_to" class="config-row">
          <span>Sent to</span><code>+{{ result.sent_to }}</code>
        </div>
        <!-- Nothing was sent (suppressed, or refused by Meta), so the number can
             only be described as the one the send was aimed at. -->
        <div v-else-if="result.requested_to" class="config-row">
          <span>Intended for</span><code>+{{ result.requested_to }}</code>
        </div>
        <div v-if="wasRedirected" class="config-row">
          <span>You typed</span><code>+{{ result.requested_to }}</code>
        </div>
        <div v-if="result.message_id" class="config-row">
          <span>Message ID</span><code>{{ result.message_id }}</code>
        </div>
        <div v-if="result.code" class="config-row">
          <span>Error code</span><code>{{ result.code }}</code>
        </div>
        <p v-if="result.message" class="result-message">{{ result.message }}</p>
        <p v-if="wasRedirected" class="hint hint-warn">
          Redirected by the non-production guard: the number you typed received
          nothing. Check the handset for +{{ result.sent_to }}.
        </p>
        <p v-if="result.success" class="hint">
          Accepted for delivery is not the same as delivered. Check the handset;
          delivery receipts need the webhook, which does not exist yet.
        </p>
        <details v-if="result.response" class="raw">
          <summary>Raw response from Meta</summary>
          <pre>{{ prettyResponse }}</pre>
        </details>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import whatsappTestApi from '@/api/whatsappTest';

const PARAM_SLOTS = 3;
const MIN_PHONE_DIGITS = 8;

export default {
  name: 'WhatsappTestPage',
  data() {
    return {
      loadingConfig: true,
      configError: '',
      configErrorHint: '',
      serverConfig: {
        configured: false,
        phone_number_id: '',
        waba_id: '',
        api_version: '',
        is_production: true,
        test_recipient_configured: false
      },
      phone: '',
      mode: 'template',
      // Defaults point at the template this account actually has. hello_world is
      // NOT auto-created on every WABA, and defaulting to a missing template
      // produces a confusing 132001 on the very first attempt.
      templateName: 'trip_starts_tomorrow',
      languageCode: 'he',
      params: new Array(PARAM_SLOTS).fill(''),
      textBody: '',
      sending: false,
      result: null,
      templates: [],
      loadingTemplates: false,
      templatesError: ''
    };
  },
  computed: {
    // The appauth store module is not namespaced, so these map globally.
    ...mapGetters( ['apptoken'] ),
    // Mirrors normalize_phone() in the backend so the number is confirmed before
    // spending a message, which is the cheapest guard against a typo.
    normalizedPhone() {
      const digits = ( this.phone || '' ).replace( /\D/g, '' );
      if ( digits.length < MIN_PHONE_DIGITS ) {
        return '';
      }
      if ( digits.startsWith( '00' ) ) {
        return digits.slice( 2 );
      }
      if ( digits.startsWith( '0' ) ) {
        return `972${digits.slice( 1 )}`;
      }
      return digits;
    },
    canSend() {
      if ( !this.normalizedPhone ) {
        return false;
      }
      if ( this.mode === 'text' ) {
        return this.textBody.trim().length > 0;
      }
      return this.templateName.trim().length > 0 && this.languageCode.trim().length > 0;
    },
    // The environment, not the credentials, decides whether a send is delivered.
    willSuppress() {
      return !this.serverConfig.is_production && !this.serverConfig.test_recipient_configured;
    },
    willRedirect() {
      return !this.serverConfig.is_production && this.serverConfig.test_recipient_configured;
    },
    wasRedirected() {
      return !!( this.result && this.result.redirected && this.result.sent_to );
    },
    resultTitle() {
      if ( this.result.suppressed ) {
        return 'Nothing was sent';
      }
      return this.result.success ? 'Accepted by Meta' : 'Failed';
    },
    resultPanelClass() {
      // Deliberately not panel-ok: a suppressed send proves nothing worked.
      if ( this.result.suppressed ) {
        return 'panel-warn';
      }
      return this.result.success ? 'panel-ok' : 'panel-error';
    },
    hasPendingTemplate() {
      return this.templates.some( t => t.status === 'PENDING' );
    },
    prettyResponse() {
      try {
        return JSON.stringify( this.result.response, null, 2 );
      } catch ( error ) {
        return String( this.result.response );
      }
    }
  },
  async mounted() {
    await this.loadConfig();
    if ( !this.configError && this.serverConfig.configured ) {
      await this.loadTemplates();
    }
  },
  methods: {
    // Built here rather than inline in the template: a literal {{n}} inside a
    // binding expression is asking the template compiler for trouble.
    paramPlaceholder( index ) {
      return `{{${index + 1}}}`;
    },
    async loadConfig() {
      this.loadingConfig = true;
      this.configError = '';
      this.configErrorHint = '';
      try {
        const response = await whatsappTestApi.getConfig( this.apptoken );
        this.serverConfig = response.data;
      } catch ( error ) {
        const status = error.response && error.response.status;
        if ( status === 404 ) {
          this.configError = 'This test page is switched off.';
          this.configErrorHint = 'Set WHATSAPP_TEST_ENABLED=true on the backend to turn it on.';
        } else if ( status === 403 ) {
          this.configError = 'Admins only.';
        } else {
          this.configError = 'Could not reach the backend.';
          this.configErrorHint = error.message;
        }
      } finally {
        this.loadingConfig = false;
      }
    },
    statusClass( status ) {
      if ( status === 'APPROVED' ) {
        return 'badge-ok';
      }
      if ( status === 'PENDING' ) {
        return 'badge-pending';
      }
      return 'badge-bad';
    },
    useTemplate( template ) {
      this.mode = 'template';
      this.templateName = template.name;
      this.languageCode = template.language;
    },
    async loadTemplates() {
      this.loadingTemplates = true;
      this.templatesError = '';
      try {
        const response = await whatsappTestApi.getTemplates( this.apptoken );
        this.templates = response.data.templates || [];
      } catch ( error ) {
        const data = error.response && error.response.data;
        this.templatesError = ( data && data.message ) || error.message;
      } finally {
        this.loadingTemplates = false;
      }
    },
    async send() {
      this.sending = true;
      this.result = null;

      const payload = { phone: this.normalizedPhone, mode: this.mode };
      if ( this.mode === 'text' ) {
        payload.body = this.textBody;
      } else {
        payload.template_name = this.templateName.trim();
        payload.language_code = this.languageCode.trim();
        payload.params = this.params.filter( param => param.trim().length > 0 );
      }

      try {
        const response = await whatsappTestApi.sendMessage( this.apptoken, payload );
        this.result = response.data;
      } catch ( error ) {
        // The interesting failures (expired token, un-whitelisted recipient)
        // arrive as 4xx/5xx with a useful body, so show the body when present.
        this.result = ( error.response && error.response.data ) || {
          success: false,
          message: error.message
        };
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style scoped>
/* This app sets html { font-size: 10px }, so a rem here is 10px, not the 16px
   these sizes were written against -- every label on the page was rendering at
   62% of its intended size. Sizes are px now so they mean what they say, and
   the base below covers the Bootstrap controls that would otherwise inherit
   the 10px root. */
.whatsapp-test-page {
  font-size: 15px;
  max-width: 640px;
  margin: 0 auto;
  padding: 24px 16px 64px;
  text-align: left;
}

.whatsapp-test-page >>> .form-control,
.whatsapp-test-page >>> .btn,
.whatsapp-test-page >>> label,
.whatsapp-test-page >>> select,
.whatsapp-test-page >>> textarea {
  font-size: 15px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 4px;
}

.page-subtitle {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
}

.panel {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
}

.panel-ok {
  border-color: #25d366;
  background: #f4fdf7;
}

.panel-warn {
  border-color: #f0ad4e;
  background: #fffaf2;
}

.panel-error {
  border-color: #d9534f;
  background: #fdf5f5;
}

.config-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 3px 0;
  font-size: 14px;
}

.config-row span {
  color: #666;
}

.field {
  display: block;
  margin-bottom: 16px;
}

.field-pair {
  display: flex;
  gap: 12px;
}

.field-pair .field {
  flex: 1;
}

.field-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
}

.field-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 15px;
}

.param-input {
  margin-bottom: 6px;
}

.radio {
  display: block;
  font-size: 14px;
  margin-bottom: 4px;
  font-weight: normal;
}

.radio-note {
  color: #888;
  font-size: 13px;
}

.hint {
  display: block;
  color: #777;
  font-size: 13px;
  margin-top: 4px;
}

.hint-warn {
  color: #b8860b;
}

.send-button {
  width: 100%;
  padding: 11px;
  border: 0;
  border-radius: 6px;
  background: #25d366;
  color: #fff;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
}

.send-button:disabled {
  background: #bbb;
  cursor: not-allowed;
}

.result-title {
  font-size: 17px;
  margin-bottom: 8px;
}

.result-message {
  font-size: 14px;
  margin: 8px 0 0;
  white-space: pre-wrap;
}

.raw {
  margin-top: 12px;
}

.raw summary {
  cursor: pointer;
  font-size: 14px;
  color: #555;
}

.raw pre {
  margin-top: 8px;
  padding: 10px;
  background: #f6f6f6;
  border-radius: 6px;
  font-size: 12px;
  overflow-x: auto;
}

code {
  font-size: 13px;
  word-break: break-all;
}

.templates-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 6px;
}

.templates {
  width: 100%;
  font-size: 14px;
  border-collapse: collapse;
}

.templates td {
  padding: 4px 6px 4px 0;
  border-bottom: 1px solid #eee;
}

.badge {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}

.badge-ok {
  background: #d7f5e3;
  color: #15683c;
}

.badge-pending {
  background: #fdf0d5;
  color: #8a5a00;
}

.badge-bad {
  background: #fadbdb;
  color: #8c2020;
}

.link-button {
  background: none;
  border: 0;
  padding: 0;
  color: #0a7cff;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
}

.link-button:disabled {
  color: #999;
  cursor: default;
}
</style>
